import { useEffect, useState, type ChangeEvent } from 'react'
import { api, type PharmacyTimeZone } from '../lib/api.ts'
import { clockIn } from '../lib/accountTimeZone.ts'
import { realignmentNotice } from '../lib/scheduleRealignment.ts'
import { TIME_ZONE_LABEL } from '../lib/vocabulary.ts'
import { EntityForm } from './EntityForm.tsx'
import { Select } from './Select.tsx'
import { useToast } from './Toast.tsx'

/**
 * The pharmacy's clock (#674), on the account's page, saved apart from
 * everything else on it: the zone its ERP's times are read on and its
 * schedules fire on. Saving moves the schedules the account already has.
 */
export function AccountTimeZone({ accountId }: { accountId: string }) {
  const [saved, setSaved] = useState<PharmacyTimeZone | undefined>()
  const [zone, setZone] = useState<PharmacyTimeZone | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  function take(next: PharmacyTimeZone) {
    setSaved(next)
    setZone(next)
  }

  useEffect(() => {
    void api
      .accountTimeZone(accountId)
      .then((r) => take(r.timeZone))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }, [accountId])

  if (error !== undefined) return <p className="text-tP text-text-error">{error}</p>
  if (saved === undefined || zone === undefined) return null

  async function save() {
    if (zone === undefined) return
    setSaving(true)
    try {
      const r = await api.saveAccountTimeZone(accountId, zone)
      take(r.timeZone)
      const notice = realignmentNotice('Fuso horário salvo', r.schedules)
      toast.show(notice.message, { tone: notice.tone, detail: notice.detail })
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao salvar', { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <EntityForm
      title="Fuso horário"
      description="O relógio da farmácia. Os horários que o ERP desta conta guarda são lidos nele, e as rotinas da conta rodam nele."
      onSave={() => void save()}
      saving={saving}
      dirty={zone !== saved}
    >
      <TimeZoneSelect zone={zone} now={new Date()} onChange={setZone} />
    </EntityForm>
  )
}

/** The select, with the zone's clock under it to check against the pharmacy's. */
export function TimeZoneSelect({
  zone,
  now,
  onChange,
}: {
  zone: PharmacyTimeZone
  now: Date
  onChange: (zone: PharmacyTimeZone) => void
}) {
  return (
    <Select
      label="Fuso horário"
      value={zone}
      helperText={`Agora são ${clockIn(zone, now)} nesse fuso.`}
      onChange={(event: ChangeEvent<HTMLSelectElement>) =>
        onChange(event.target.value as PharmacyTimeZone)
      }
    >
      {(Object.keys(TIME_ZONE_LABEL) as PharmacyTimeZone[]).map((one) => (
        <option key={one} value={one}>
          {TIME_ZONE_LABEL[one]}
        </option>
      ))}
    </Select>
  )
}
